import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Avatar,
  Divider,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../utils/dateUtils';
import { useAuth } from '../../../hooks';
import { hasuraClient } from '../../../utils/graphqlClient';
import {
  GET_REVENUE_CALCULATION_RULE_MATCH_QUERY,
  GET_REVENUE_CALCULATION_RULE_MATCH_FOR_SALES_QUERY,
} from '../../../graphql/queries';
import { computeQuarterAmount } from '../../../utils/revenueCalculation';

const STATUS_CONFIG = {
  APPROVED: {
    color: 'success',
    icon: <CheckCircleOutlinedIcon fontSize="small" />,
    labelKey: 'userRevenueEntries.status.approved',
  },
  REJECTED: {
    color: 'error',
    icon: <CancelOutlinedIcon fontSize="small" />,
    labelKey: 'userRevenueEntries.status.rejected',
  },
  PENDING: {
    color: 'warning',
    icon: null,
    labelKey: 'userRevenueEntries.status.pending',
  },
};

const CHANNEL_MAP = {
  SATIS_CIROSU: 'SATIS',
  PROJE_YAZIM_CIROSU: 'YAZIM',
  ONAY_SONRASI_CIROSU: 'ONAY_SONRASI',
};

// secili_aylar (INT[]) → "Ocak, Şubat, ..." (sirali, uniq, valid range).
// Bos veya array degilse null doner; caller null'da satiri gostermez.
const formatSelectedMonths = (arr, t) => {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const labels = arr
    .map((n) => Number(n))
    .filter((n) => n >= 1 && n <= 12)
    .sort((a, b) => a - b)
    .map((n) => t(`userRevenueEntries.form.months.${n}`));
  return labels.length > 0 ? labels.join(', ') : null;
};

const InfoRow = ({ icon, label, value, valueColor }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
    <Box sx={{ color: 'text.disabled', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        display="block"
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        color={valueColor || 'text.primary'}
      >
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

// Ciro talebi detay modal'ı.
// - Hedefe Esas Ciro = tutar × bonus_revenue_type.coefficient. Coefficient
//   page tarafindan props uzerinden gecirilir (relationship metadata'da yok).
// - ONAY_SONRASI_CIROSU icin x/y ay gostergesi: revenue_calculation_rules
//   eslesmesi + computeQuarterAmount ile hesaplanir. Eslesme/kural yoksa
//   sessizce gizlenir (hata gosterilmez — kullanici talepleri okumaya devam
//   edebilir).
const CiroTalepDetailModal = ({
  open,
  onClose,
  entry,
  coefficient = 1,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [xyText, setXyText] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setXyText(null);
    if (!open || !entry) return undefined;
    if (entry.ciro_kanali !== 'ONAY_SONRASI_CIROSU') return undefined;

    // İlk fallback: entry.ay_sayisi kayitliysa hesap yapilamasa bile en az
    // "6 ay" olarak goster. Alt kismindaki asenkron hesap basarili olursa
    // ustune "6/12 ay" seklinde yazar.
    if (Number.isFinite(Number(entry?.ay_sayisi))) {
      setXyText(`${Number(entry.ay_sayisi)} ay`);
    }

    const job = entry.job;
    const contract = entry.contract
      || (job?.contract_id ? { id: job.contract_id, renewals: job?.contract?.renewals || [] } : null);
    const jobTypeId = job?.job_type_id || null;
    const pricingMethodId = contract?.pricing_method_id || null;
    const companyId = user?.companyId || entry.company_id;
    const revenueChannel = CHANNEL_MAP[entry.ciro_kanali];

    if (!companyId || !revenueChannel) return undefined;
    // Pricing method yoksa kural lookup'i guvenli sekilde dener — sonuc
    // bossa breakdown ekrana yansitilmaz. Bu detay modali (read-only).
    const useSalesNullQuery = entry.ciro_kanali === 'SATIS_CIROSU' && !jobTypeId;
    const query = useSalesNullQuery
      ? GET_REVENUE_CALCULATION_RULE_MATCH_FOR_SALES_QUERY
      : GET_REVENUE_CALCULATION_RULE_MATCH_QUERY;
    const params = useSalesNullQuery
      ? { companyId, revenueChannel, pricingMethodId }
      : { companyId, jobTypeId, revenueChannel, pricingMethodId };

    if (!pricingMethodId || (!useSalesNullQuery && !jobTypeId)) {
      // Yetersiz girdi — sessizce vazgec.
      return undefined;
    }

    (async () => {
      try {
        const result = await hasuraClient.query(query, params);
        const rule = result?.revenue_calculation_rules?.[0];
        if (!rule) return; // kural yok → goster_me
        let approvalDate = null;
        if (Array.isArray(job?.job_details)) {
          for (const d of job.job_details) {
            const dd = d?.detail_data;
            if (dd?.approvalDate) { approvalDate = dd.approvalDate; break; }
          }
        }
        const computed = computeQuarterAmount({
          rule,
          contract,
          job,
          approvalDate,
          yil: entry.yil,
          ceyrek: entry.ceyrek,
          existingEntries: [],
          currentEntryId: entry.id,
        });
        if (cancelled) return;
        if (computed && computed.y) {
          // KAYITLI ay_sayisi (kullanicinin girdiği veya sistem tarafından
          // otomatik yazilan) pay olarak gosterilir; sistem'in kural bazli
          // computed.x fallback. Kayitta 6 yazan 6/12 gorur, degistirmedeyse
          // sistemin hesabini.
          const paydali = Number.isFinite(Number(entry?.ay_sayisi))
            ? Number(entry.ay_sayisi)
            : (computed.x ?? 0);
          setXyText(`${paydali}/${computed.y} ay`);
        }
      } catch (e) {
        // Sessiz fallback — modal okuma deneyimini bozmasin.
        // eslint-disable-next-line no-console
        console.warn('[CiroDetail] x/y hesaplama atlandi', e?.message || e);
      }
    })();

    return () => { cancelled = true; };
  }, [open, entry, user?.companyId]);

  if (!entry) return null;

  const statusCfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.PENDING;

  const userName = entry.user
    ? `${entry.user.first_name || ''} ${entry.user.last_name || ''}`.trim()
    : entry.user_name || '—';

  const initials =
    userName && userName !== '—'
      ? userName
          .split(' ')
          .map((n) => n[0])
          .slice(0, 2)
          .join('')
          .toUpperCase()
      : '?';

  const formattedAmount = `${Number(entry.tutar || 0).toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ₺`;

  const targetRevenue = Number(entry.tutar || 0) * (Number.isFinite(coefficient) ? coefficient : 1);
  const formattedTargetRevenue = `${targetRevenue.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ₺`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'primary.main',
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {userName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {entry.ciro_kanali_label || entry.ciro_kanali || '—'}
            </Typography>
          </Box>
          <Chip
            icon={statusCfg.icon}
            label={t(statusCfg.labelKey)}
            color={statusCfg.color}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <InfoRow
            icon={<PaidOutlinedIcon fontSize="small" />}
            label={t('userRevenueEntries.table.tutar', 'Tutar')}
            value={formattedAmount}
            valueColor="success.main"
          />
          <InfoRow
            icon={<TrendingUpOutlinedIcon fontSize="small" />}
            label={t('userRevenueEntries.detail.targetRevenue', 'Hedefe Esas Ciro')}
            value={formattedTargetRevenue}
            valueColor="primary.main"
          />
          {entry.ciro_kanali === 'ONAY_SONRASI_CIROSU' && (
            <InfoRow
              icon={<CalendarTodayOutlinedIcon fontSize="small" />}
              label={t('userRevenueEntries.detail.xyMonths', 'Toplam Prim Dönemi')}
              value={xyText || t('userRevenueEntries.detail.xyMonthsNA', 'Hesaplanamadı')}
              valueColor={xyText ? undefined : 'text.disabled'}
            />
          )}
          {entry.ciro_kanali === 'ONAY_SONRASI_CIROSU'
            && Array.isArray(entry.secili_aylar)
            && entry.secili_aylar.length > 0
            && formatSelectedMonths(entry.secili_aylar, t) && (
              <InfoRow
                icon={<CalendarTodayOutlinedIcon fontSize="small" />}
                label={t('userRevenueEntries.form.seciliAylar', 'Prim Alınan Aylar')}
                value={formatSelectedMonths(entry.secili_aylar, t)}
              />
            )}
          <InfoRow
            icon={<CalendarTodayOutlinedIcon fontSize="small" />}
            label={t('userRevenueEntries.table.period', 'Dönem')}
            value={
              entry.period_label ||
              (entry.yil && entry.ceyrek
                ? `${entry.yil} / Q${entry.ceyrek}`
                : '—')
            }
          />
          <InfoRow
            icon={<BusinessOutlinedIcon fontSize="small" />}
            label={t('userRevenueEntries.table.firm', 'Firma')}
            value={entry.firm_full_name || entry.firm_name}
          />
          <InfoRow
            icon={<PersonOutlineOutlinedIcon fontSize="small" />}
            label={t('userRevenueEntries.table.person', 'Ekleyen')}
            value={userName}
          />
          <InfoRow
            icon={<DescriptionOutlinedIcon fontSize="small" />}
            label={t('userRevenueEntries.table.referans', 'Referans')}
            value={entry.referans}
          />
          {entry.aciklama && (
            <InfoRow
              icon={<NotesOutlinedIcon fontSize="small" />}
              label={t('userRevenueEntries.table.aciklama', 'Açıklama')}
              value={entry.aciklama}
            />
          )}
          {entry.status === 'REJECTED' && entry.reject_reason && (
            <InfoRow
              icon={<CancelOutlinedIcon fontSize="small" />}
              label={t(
                'userRevenueEntries.rejectReasonLabel',
                'Reddedilme nedeni',
              )}
              value={entry.reject_reason}
              valueColor="error.main"
            />
          )}

          <Divider sx={{ my: 0.5 }} />

          <InfoRow
            icon={<CalendarTodayOutlinedIcon fontSize="small" />}
            label={t('common.createdAt', 'Oluşturulma')}
            value={entry.created_at ? formatDateTime(entry.created_at) : '—'}
          />
          {entry.updated_at && entry.updated_at !== entry.created_at && (
            <InfoRow
              icon={<CalendarTodayOutlinedIcon fontSize="small" />}
              label={t('common.updatedAt', 'Güncellenme')}
              value={formatDateTime(entry.updated_at)}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} variant="text" color="inherit">
          {t('common.close', 'Kapat')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CiroTalepDetailModal;
